import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Trash2, Clock, Sparkles, X, ChevronRight, Diamond, Music2, Trophy, Waves, Moon } from 'lucide-react';
import axios from 'axios';
import { voiceVolume, getStoredAudioMode } from '../../utils/modeAudio';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Archetype icons and colors
const ARCHETYPE_CONFIG = {
  billionaire: { icon: Diamond, color: '#60A5FA', name: 'Billionaire', emoji: '💎' },
  rockstar: { icon: Music2, color: '#F472B6', name: 'Rockstar', emoji: '🎤' },
  champion: { icon: Trophy, color: '#FBBF24', name: 'Champion', emoji: '🏆' }
};

const MyStates = ({ onClose, onPlayState, getToken }) => {
  const [regularStates, setRegularStates] = useState([]);
  const [realityStates, setRealityStates] = useState([]);
  const [alignmentStates, setAlignmentStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [playingId, setPlayingId] = useState(null);
  const [nightMode, setNightMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const audioRef = useRef(null);
  const musicRef = useRef(null);

  useEffect(() => {
    loadAllStates();
    return () => {
      if (audioRef.current) audioRef.current.pause();
      if (musicRef.current) musicRef.current.pause();
    };
  }, []);

  const loadAllStates = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statesRes, realitiesRes, alignmentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/private-suite/states`, { headers }).catch(() => ({ data: { states: [] } })),
        axios.get(`${API_URL}/api/private-suite/reality-states`, { headers }).catch(() => ({ data: { states: [] } })),
        axios.get(`${API_URL}/api/instant-alignment/saved`, { headers }).catch(() => ({ data: { sessions: [] } }))
      ]);
      
      setRegularStates(statesRes.data.states || statesRes.data || []);
      setRealityStates(realitiesRes.data.states || []);
      setAlignmentStates((alignmentsRes.data.sessions || []).map(s => ({
        ...s,
        id: s.session_id,
        name: s.saved_name || s.intention,
        type: 'alignment',
        created_at: s.created_at,
      })));
    } catch (err) {
      console.error('Error loading states:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayRegularState = async (state) => {
    if (playingId === state.id) {
      // Stop playing
      if (audioRef.current) audioRef.current.pause();
      setPlayingId(null);
      return;
    }

    try {
      // Call the generate-session endpoint to create the audio
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/generate-session`,
        {
          desired_state: state.desired_state,
          affirmations: state.affirmations,
          duration: state.duration || 180,
          voice_type: state.voice_type || 'female_calm',
          subliminal: state.subliminal || 'off'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.audio_url) {
        setPlayingId(state.id);
        if (audioRef.current) {
          audioRef.current.src = `${API_URL}${response.data.audio_url}`;
          audioRef.current.play();
          audioRef.current.onended = () => setPlayingId(null);
        }
        
        // Record usage
        axios.post(`${API_URL}/api/private-suite/states/${state.id}/use`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
    } catch (err) {
      console.error('Error playing state:', err);
      setPlayingId(null);
    }
  };

  const handlePlayRealityState = (state) => {
    if (playingId === state.id) {
      if (audioRef.current) audioRef.current.pause();
      if (musicRef.current) musicRef.current.pause();
      setPlayingId(null);
      return;
    }

    setPlayingId(state.id);
    
    // Play voice recording
    if (audioRef.current && state.voiceRecordingUrl) {
      audioRef.current.src = state.voiceRecordingUrl;
      audioRef.current.volume = state.settings?.voiceVolume || 0.7;
      audioRef.current.play().catch(() => {});
      audioRef.current.onended = () => setPlayingId(null);
    }
    
    // Play background music
    if (musicRef.current && state.musicTrack?.url) {
      musicRef.current.src = state.musicTrack.url;
      musicRef.current.volume = state.settings?.musicVolume || 0.3;
      musicRef.current.loop = true;
      musicRef.current.play().catch(() => {});
    }

    // Record usage
    const token = getToken();
    axios.post(`${API_URL}/api/private-suite/reality-states/${state.id}/use`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {});
  };

  const handlePlayAlignment = (state) => {
    if (playingId === state.id && !nightMode) {
      if (audioRef.current) audioRef.current.pause();
      setPlayingId(null);
      return;
    }

    setPlayingId(state.id);

    if (audioRef.current && state.audio_url) {
      audioRef.current.src = `${API_URL}${state.audio_url}`;
      audioRef.current.loop = nightMode;
      audioRef.current.volume = 0;
      const voiceTarget = voiceVolume(getStoredAudioMode());
      audioRef.current.play().then(() => {
        let vol = 0;
        const fade = setInterval(() => {
          vol += 0.01;
          if (vol >= voiceTarget || !audioRef.current) {
            clearInterval(fade);
            if (audioRef.current) audioRef.current.volume = voiceTarget;
          } else if (audioRef.current) {
            audioRef.current.volume = vol;
          }
        }, 40);
      }).catch(() => {});
      if (!nightMode) {
        audioRef.current.onended = () => setPlayingId(null);
      }
    }
  };

  const toggleNightMode = () => {
    setNightMode(prev => {
      const next = !prev;
      if (audioRef.current) {
        audioRef.current.loop = next;
      }
      return next;
    });
  };

  const handleDelete = async (type, id) => {
    try {
      const token = getToken();
      if (type === 'alignment') {
        // Unsave alignment
        await axios.post(`${API_URL}/api/instant-alignment/unsave`, 
          { session_id: id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAlignmentStates(prev => prev.filter(s => s.id !== id));
      } else {
        const endpoint = type === 'reality' 
          ? `/api/private-suite/reality-states/${id}`
          : `/api/private-suite/states/${id}`;
        
        await axios.delete(`${API_URL}${endpoint}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (type === 'reality') {
          setRealityStates(prev => prev.filter(s => s.id !== id));
        } else {
          setRegularStates(prev => prev.filter(s => s.id !== id));
        }
      }
      if (playingId === id && audioRef.current) {
        audioRef.current.pause();
        setPlayingId(null);
      }
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting state:', err);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '3 min';
    const mins = Math.floor(seconds / 60);
    return `${mins} min`;
  };

  const allStates = [
    ...realityStates.map(s => ({ ...s, type: 'reality' })),
    ...regularStates.map(s => ({ ...s, type: 'regular' })),
    ...alignmentStates,
  ].sort((a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at));

  const displayStates = activeTab === 'realities' ? realityStates.map(s => ({ ...s, type: 'reality' }))
    : activeTab === 'states' ? regularStates.map(s => ({ ...s, type: 'regular' }))
    : activeTab === 'alignments' ? alignmentStates
    : allStates;

  const isEmpty = displayStates.length === 0;

  return (
    <div className="fixed inset-0 bg-[#0a0a12] z-50 flex flex-col">
      <audio ref={audioRef} />
      <audio ref={musicRef} />

      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-white/10">
        <h1 className="text-2xl font-light text-white">My States</h1>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          data-testid="close-my-states-btn"
        >
          <X size={20} className="text-white/60" />
        </button>
      </div>

      {/* Tabs + Night Mode */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
        <div className="flex gap-2 overflow-x-auto">
          {[
            { id: 'all', label: 'All' },
            { id: 'alignments', label: 'Alignments', count: alignmentStates.length },
            { id: 'realities', label: 'Realities', count: realityStates.length },
            { id: 'states', label: 'States', count: regularStates.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-purple-500/20 text-white border border-purple-500/40'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="ml-1.5 text-white/40">({tab.count})</span>
              )}
            </button>
          ))}
        </div>

        {/* Night Mode toggle */}
        <button
          onClick={toggleNightMode}
          className={`ml-3 flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
            nightMode
              ? 'bg-indigo-500/25 text-indigo-300 border border-indigo-500/40'
              : 'bg-white/5 text-white/40 hover:bg-white/10'
          }`}
          data-testid="night-mode-toggle"
        >
          <Moon size={14} />
          <span className="hidden sm:inline">Night</span>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4 pb-40">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
          </div>
        ) : isEmpty ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Sparkles size={28} className="text-white/30" />
            </div>
            <h3 className="text-white/60 text-lg mb-2">No states yet</h3>
            <p className="text-white/40 text-sm max-w-xs mx-auto">
              {activeTab === 'realities' 
                ? 'Create your first Reality in "Tune In To Reality"'
                : activeTab === 'states'
                ? 'Create a custom state in any mode'
                : 'Your saved states and realities will appear here'}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {displayStates.map((state, index) => {
              const isReality = state.type === 'reality';
              const isAlignment = state.type === 'alignment';
              const archetype = isReality ? ARCHETYPE_CONFIG[state.archetypeId] : null;
              const isPlaying = playingId === state.id;
              
              return (
                <motion.div
                  key={state.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`relative p-4 rounded-2xl transition-all ${
                    isPlaying 
                      ? isAlignment
                        ? 'bg-amber-500/15 border border-amber-500/30'
                        : 'bg-purple-500/20 border border-purple-500/40'
                      : 'bg-white/5 border border-transparent hover:bg-white/10'
                  }`}
                  data-testid={`state-card-${state.id}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ 
                        backgroundColor: isAlignment
                          ? 'rgba(210,175,130,0.12)'
                          : isReality 
                            ? `${archetype?.color}20` 
                            : 'rgba(147, 51, 234, 0.2)'
                      }}
                    >
                      {isAlignment ? (
                        <Waves size={22} strokeWidth={1.2} className="text-[#D2AF82]/70" />
                      ) : isReality ? (
                        <span className="text-xl">{archetype?.emoji}</span>
                      ) : (
                        <span className="text-xl">✨</span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">
                        {state.name || state.saved_name || state.desired_state || 'Unnamed State'}
                      </h3>
                      <div className="flex items-center gap-2 text-white/40 text-xs mt-1">
                        <span className={`px-2 py-0.5 rounded-full ${
                          isAlignment ? 'bg-amber-500/20 text-amber-300'
                          : isReality ? 'bg-violet-500/20 text-violet-300' 
                          : 'bg-blue-500/20 text-blue-300'
                        }`}>
                          {isAlignment ? 'Alignment' : isReality ? archetype?.name || 'Reality' : 'State'}
                        </span>
                        {isAlignment && state.category && (
                          <span className="text-white/30 capitalize">{state.category}</span>
                        )}
                        {!isAlignment && (
                          <span className="flex items-center gap-1">
                            <Clock size={10} />
                            {formatDuration(state.duration || state.settings?.duration)}
                          </span>
                        )}
                        {nightMode && isPlaying && (
                          <span className="flex items-center gap-1 text-indigo-300">
                            <Moon size={10} />
                            Looping
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Play button */}
                    <button
                      onClick={() => isAlignment ? handlePlayAlignment(state) : isReality ? handlePlayRealityState(state) : handlePlayRegularState(state)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                        isPlaying
                          ? isAlignment
                            ? 'bg-amber-600/80 shadow-lg shadow-amber-600/30'
                            : 'bg-purple-500 shadow-lg shadow-purple-500/40'
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                      data-testid={`play-state-${state.id}`}
                    >
                      {isPlaying ? (
                        <Pause size={20} className="text-white" />
                      ) : (
                        <Play size={20} className="text-white ml-0.5" />
                      )}
                    </button>

                    {/* Delete button */}
                    <button
                      onClick={() => setDeleteConfirm(state.id)}
                      className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/20 transition-colors"
                      data-testid={`delete-state-${state.id}`}
                    >
                      <Trash2 size={16} className="text-white/40 hover:text-red-400" />
                    </button>
                  </div>

                  {/* Playing indicator */}
                  {isPlaying && (
                    <motion.div
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full origin-left ${
                        isAlignment
                          ? 'bg-gradient-to-r from-amber-500 to-amber-300'
                          : 'bg-gradient-to-r from-purple-500 to-violet-500'
                      }`}
                    />
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-60 p-6"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#1a1a2e] rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="text-white text-lg font-medium mb-2">Delete State?</h3>
              <p className="text-white/60 text-sm mb-6">
                This cannot be undone. Your recordings and settings will be lost.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const state = displayStates.find(s => s.id === deleteConfirm);
                    handleDelete(state?.type || 'regular', deleteConfirm);
                  }}
                  className="flex-1 py-3 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyStates;
